import { Button, CircularProgress, Paper, Snackbar, Typography } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { FileUploadDialog } from 'components'
import { AUXILIAR_TITLE, getClosingDatesService, ROOT_TITLE } from 'lib'
import { uploadExcelFile } from 'lib/utils/api/excelfile'
import moment from 'moment'
import 'moment/locale/es'
import React from 'react'
import { Helmet } from 'react-helmet'
import { IClosingDate, IClosingMonth, IMessageConfig } from 'types'

moment.locale('es')

const ExcelRetentionPage = () => {
    const [openDialog, setOpenDialog] = React.useState<boolean>(false)
    const [loadingDates, setLoadingDates] = React.useState<boolean>(true)
    const [activeClosingMonth, setActiveClosingMonth] = React.useState<IClosingMonth | null>(null)
    const [messageConfig, setMessageConfig] = React.useState<IMessageConfig>({ open: false, message: '', severity: 'info' })

    const checkCurrentClosingInterval = React.useCallback(async () => {
        try {
            setLoadingDates(true)

            const closingDates: IClosingDate[] = await getClosingDatesService()
            const currentDate = new Date()
            const currentYear = currentDate.getFullYear()
            const currentMonth = currentDate.getMonth()

            const currentClosingYear = closingDates.find(item => item.year === currentYear)
            const currentClosingMonth = currentClosingYear?.closingMonths.find(item => item.month === currentMonth)

            if (!currentClosingMonth?.openingDate || !currentClosingMonth?.closingDate) {
                setActiveClosingMonth(null)
                return
            }

            const openingDate = new Date(currentClosingMonth.openingDate)
            const closingDate = new Date(currentClosingMonth.closingDate)

            if (isNaN(openingDate.getTime()) || isNaN(closingDate.getTime())) {
                setActiveClosingMonth(null)
                return
            }

            const isCurrentDateInInterval = currentDate.getTime() >= openingDate.getTime() && currentDate.getTime() <= closingDate.getTime()

            setActiveClosingMonth(isCurrentDateInInterval ? currentClosingMonth : null)
        } catch (error) {
            setActiveClosingMonth(null)
            setMessageConfig({ open: true, message: 'No se pudo consultar el periodo de cierre.', severity: 'error' })
        } finally {
            setLoadingDates(false)
        }
    }, [])

    React.useEffect(() => {
        checkCurrentClosingInterval()
    }, [checkCurrentClosingInterval])

    const handleUpload = async (file: File) => {
        try {
            const status = await uploadExcelFile(file)

            setOpenDialog(false)

            if (status === 200) {
                setMessageConfig({ open: true, message: 'El archivo cargado será procesado a continuación.', severity: 'info' })
            }

            if (status === 409) {
                setMessageConfig({ open: true, message: 'Existe en este momento un archivo procesándose.', severity: 'warning' })
            }
        } catch (error) {
            setOpenDialog(false)
            setMessageConfig({ open: true, message: 'El archivo cargado no pudo ser procesado.', severity: 'error' })
        }
    }

    return (
        <Paper className="flex flex-col h-full overflow-hidden p-4 pb-0">
            <Helmet>
                <title>{`${ROOT_TITLE} - ${AUXILIAR_TITLE}`}</title>
            </Helmet>

            <div className="flex items-center justify-between">
                {loadingDates ? (
                    <div className="flex items-center" style={{ gap: 10 }}>
                        <CircularProgress size={22} />
                        <Typography color="textSecondary" variant="body2">Consultando periodo de cierre...</Typography>
                    </div>
                ) : activeClosingMonth ? (
                    <Paper variant="outlined" style={{ width: '100%', maxWidth: 600, padding: 16, borderLeft: '5px solid #e30613', backgroundColor: '#fafafa' }}>
                        <Typography style={{ fontWeight: 700, marginBottom: 8 }} variant="subtitle1">Periodo de cierre activo</Typography>

                        <Typography color="textSecondary" variant="body2">La fecha actual se encuentra dentro del intervalo configurado.</Typography>

                        <div className="flex items-center" style={{ gap: 24, marginTop: 12 }}>
                            <div>
                                <Typography color="textSecondary" variant="caption">APERTURA</Typography>
                                <Typography variant="body1">{moment(activeClosingMonth.openingDate).format('DD-MM-YYYY HH:mm')}</Typography>
                            </div>

                            <div>
                                <Typography color="textSecondary" variant="caption">CIERRE</Typography>
                                <Typography variant="body1">{moment(activeClosingMonth.closingDate).format('DD-MM-YYYY HH:mm')}</Typography>
                            </div>
                        </div>
                    </Paper>
                ) : (
                    <Button size="small" variant="contained" color="primary" disableElevation onClick={() => setOpenDialog(true)}>Subir Archivo Excel</Button>
                )}
            </div>

            <Snackbar open={messageConfig.open} autoHideDuration={6000} onClose={() => setMessageConfig(current => ({ ...current, open: false }))} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                <Alert variant="filled" onClose={() => setMessageConfig(current => ({ ...current, open: false }))} severity={messageConfig.severity}>{messageConfig.message}</Alert>
            </Snackbar>

            <FileUploadDialog open={openDialog} onClose={() => setOpenDialog(false)} onAccept={files => handleUpload(files[0].file)} singleFile hideDescription />
        </Paper>
    )
}

export default ExcelRetentionPage